import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router';
import ReactMarkdown from 'react-markdown';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

// Styled component for list items
const StyledListItem = styled('li')(({ theme }) => {
  return {
    marginTop: theme.spacing(1),
  };
});

// Component to render list items with proper styling
const ListItem = ({ ordered: _ordered = undefined, ...props }: any) => {
  return (
    <StyledListItem>
      <Typography component="span" {...props} />
    </StyledListItem>
  );
};

ListItem.propTypes = {
  ordered: PropTypes.bool,
};

// Updated to use the components API for react-markdown v6
const components = {
  h1: (props) => {
    return <Typography {...props} gutterBottom variant="h4" />;
  },
  h2: (props) => {
    return <Typography {...props} gutterBottom variant="h6" />;
  },
  h3: (props) => {
    return <Typography {...props} gutterBottom variant="subtitle1" />;
  },
  h4: (props) => {
    return <Typography {...props} gutterBottom variant="caption" paragraph />;
  },
  h5: (props) => {
    return <Typography {...props} gutterBottom variant="body2" />;
  },
  h6: (props) => {
    return <Typography {...props} gutterBottom variant="body2" />;
  },
  li: ListItem,
  p: (props) => {
    return <Typography {...props} paragraph />;
  },
  a: (props) => {
    const { href, children } = props;
    if (href.startsWith('http')) {
      return (
        <Link
          href={href}
          target="_blank"
          rel="noreferrer"
          color="primary"
          underline="hover"
        >
          {children}
        </Link>
      );
    }
    if (href.startsWith('mailto')) {
      return (
        <Link href={href} color="primary" underline="hover">
          {children}
        </Link>
      );
    }
    return (
      <Link component={RouterLink} to={href} color="primary" underline="hover">
        {children}
      </Link>
    );
  },
};

// react-markdown v10 removed `className` support and hard-asserts if it is
// passed (devlop's `unreachable()`). devlop no-ops under the `default`
// export condition, so the only PRODUCTION symptom was silently-dropped
// styling; in dev/test it is a hard throw that white-screens /calculators
// and /user-guide. Both consumers wrap this component in MUI `styled()`,
// which injects className. Apply it to a wrapping div - exactly what
// react-markdown v6 did internally when this code was written - instead of
// forwarding it.
export default function Markdown({ className = undefined, ...props }) {
  return (
    <div className={className}>
      <ReactMarkdown components={components} {...props} />
    </div>
  );
}

Markdown.propTypes = {
  className: PropTypes.string,
};
